<script setup lang="ts">
import { computed } from 'vue';

import type { ChartGranularity } from './chart-point-window';

import { formatChartAxisLabel } from './chart-point-window';
import { buildLabelIndexes } from './chart-scales';
import ChartCanvas from './ChartCanvas.vue';
import ChartTooltip from './ChartTooltip.vue';
import { useBarChartScale } from './use-bar-chart-scale';
import { useChartCanvas } from './use-chart-canvas';

interface OverlayBarChartPoint {
  date: string;
  isGap?: boolean;
  tooltip: string;
  total: number;
  value: number;
}

interface Props {
  granularity?: ChartGranularity;
  label?: string;
  points: OverlayBarChartPoint[];
  valueFormatter?: (value: number) => string;
}

const props = withDefaults(defineProps<Props>(), {
  granularity: 'Day',
  label: 'Показатель по дням',
  valueFormatter: (value: number) => Intl.NumberFormat('ru-RU').format(value),
});

const { activeIndex, canvas, geometry } = useChartCanvas(
  'overlay-bar-chart',
  () => props.label,
  () => props.points.length,
);

const { valueScale, valueTicks, pointSlotWidth, centerScale, barHeight } = useBarChartScale(
  () => props.points.flatMap((point) => [point.total, point.value]),
  () => props.points.length,
  geometry,
);
const barWidth = computed(() => Math.max(1, Math.min(18, pointSlotWidth.value * 0.62)));
const groups = computed(() =>
  props.points.map((point, index) => {
    const centerX = centerScale.value(index);

    return {
      centerX,
      index,
      point,
      totalHeight: barHeight(point.total),
      valueHeight: barHeight(point.value),
      x: centerX - barWidth.value / 2,
    };
  }),
);
const activeGroup = computed(() => (activeIndex.value === null ? null : (groups.value[activeIndex.value] ?? null)));
const visibleLabelIndexes = computed(() => buildLabelIndexes(props.points.length));
</script>

<template>
  <ChartCanvas
    v-bind="canvas"
    :geometry="geometry"
    :ticks="valueTicks"
    :scale="valueScale"
    :formatter="valueFormatter"
  >
    <g
      v-for="group in groups"
      :key="group.point.date"
    >
      <template v-if="!group.point.isGap">
        <rect
          :x="group.x"
          :y="geometry.plotBottom - group.totalHeight"
          :width="barWidth"
          :height="group.totalHeight"
          rx="2"
          fill="var(--border-soft)"
        />
        <rect
          :x="group.x"
          :y="geometry.plotBottom - group.valueHeight"
          :width="barWidth"
          :height="group.valueHeight"
          rx="2"
          fill="var(--accent)"
        />
      </template>

      <text
        v-else
        :x="group.centerX"
        :y="geometry.plotBottom - 5"
        text-anchor="start"
        fill="var(--text-secondary)"
        font-size="9"
        :transform="`rotate(-90 ${group.centerX} ${geometry.plotBottom - 5})`"
      >
        выходной
      </text>

      <text
        v-if="visibleLabelIndexes.has(group.index)"
        :x="group.centerX"
        :y="geometry.xLabelY"
        text-anchor="middle"
        fill="var(--text-secondary)"
        font-size="12"
      >
        {{ formatChartAxisLabel(group.point.date, granularity) }}
      </text>
    </g>
    <template #tooltip>
      <ChartTooltip
        v-if="activeGroup"
        :anchor-percent="(activeGroup.centerX / geometry.viewWidth) * 100"
      >
        {{ activeGroup.point.tooltip }}
      </ChartTooltip>
    </template>
  </ChartCanvas>
</template>
